import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AssistantCreativeDraft,
  KnowledgeDocumentListItem,
} from '@digital-twins/shared-types'

import { ArtifactEditorShell } from '@/components/artifact-editor/ArtifactEditorShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/Text'
import { ThreadAssetView } from '@/lib/api'
import { cn } from '@/lib/utils'

import {
  CREATIVE_DRAFT_AUTOSAVE_MS,
  creativeDraftCardTestId,
  formatCreativeDraftTitle,
  isCreativeDraftConfirmed,
} from './formatCreativeDraftTitle'
import { ArtifactPreviewCard } from './ThreadRunPane/ArtifactPreviewCard'
import { ThreadArtifactItem } from './ThreadRunPane/collectThreadArtifacts'
import { ThreadArtifactBodyPreview } from './ThreadRunPane/ThreadArtifactBodyPreview'

interface ThreadCreativeDraftSetProps {
  drafts: Array<AssistantCreativeDraft>
  onAutosave?: (
    key: string,
    patch: { title: string; body: string },
  ) => void | Promise<void>
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void
  onExpand?: (item: ThreadArtifactItem) => void
  isConfirming?: boolean
  isDraftSaved?: boolean
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  className?: string
}

interface DraftEdit {
  title: string
  body: string
}

interface ThreadCreativeDraftCardProps {
  draft: AssistantCreativeDraft
  title: string
  body: string
  cardTitle: string
  letter: string
  index: number
  isPeeking: boolean
  isEditing: boolean
  canEdit: boolean
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onEdit: () => void
  onDone: () => void
  onCancelEdit: () => void
  onExpand?: (item: ThreadArtifactItem) => void
  onTitleChange: (title: string) => void
  onBodyChange: (body: string) => void
}

export function toPreviewItem(
  draft: AssistantCreativeDraft,
  body: string,
  index: number,
): ThreadArtifactItem {
  const item: ThreadArtifactItem = {
    key: draft.key,
    title: draft.title,
    body,
    slug: 'creative-draft',
    stepId: 'creative-draft',
    stepIndex: 0,
    artifactIndex: index,
  }
  if (draft.publicId) {
    item.publicId = draft.publicId
  }
  if (draft.kind === 'creative') {
    item.kind = 'creative'
  }
  return item
}

function ThreadCreativeDraftCard(props: ThreadCreativeDraftCardProps) {
  const {
    draft,
    title,
    body,
    cardTitle,
    letter,
    index,
    isPeeking,
    isEditing,
    canEdit,
    threadAssets,
    docsByToken,
    onEdit,
    onDone,
    onCancelEdit,
    onExpand,
    onTitleChange,
    onBodyChange,
  } = props

  const { t } = useTranslation('app')
  const letterKey = letter.toLowerCase()
  const previewItem = toPreviewItem(draft, body, index)

  function handleExpand() {
    if (onExpand) {
      onExpand(previewItem)
    }
  }

  const editor = isEditing ? (
    <div className="mt-2 flex flex-col gap-2">
      <Input
        value={title}
        aria-label={t('threads.detail.creativeDraft.headlineAria')}
        data-testid={`thread-creative-draft-title-${letterKey}`}
        onChange={(event) => {
          onTitleChange(event.target.value)
        }}
      />
      <ArtifactEditorShell
        value={body}
        accessibleName={cardTitle}
        canEdit
        isEditing
        peek={false}
        onChange={onBodyChange}
        onDone={onDone}
        onCancel={onCancelEdit}
        onExpand={onExpand ? handleExpand : undefined}
        testIdPrefix="artifact-preview-card"
      />
    </div>
  ) : (
    <ArtifactPreviewCard
      item={previewItem}
      collapsedByDefault={false}
      clamp={false}
      peek={isPeeking}
      showHeader={false}
      canEdit={canEdit}
      onEdit={
        canEdit
          ? () => {
              onEdit()
            }
          : undefined
      }
      onExpand={onExpand ? handleExpand : undefined}
      className="mt-2"
    >
      <ThreadArtifactBodyPreview
        body={body}
        threadAssets={threadAssets}
        docsByToken={docsByToken}
      />
    </ArtifactPreviewCard>
  )

  return (
    <div
      className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3"
      data-testid={creativeDraftCardTestId(letter)}
    >
      <Text
        as="span"
        size="sm"
        data-testid={`thread-creative-draft-label-${letterKey}`}
      >
        {cardTitle}
      </Text>
      {editor}
    </div>
  )
}

export function ThreadCreativeDraftSet(props: ThreadCreativeDraftSetProps) {
  const {
    drafts,
    onAutosave,
    onConfirm,
    onCancel,
    onExpand,
    isConfirming = false,
    isDraftSaved = false,
    threadAssets,
    docsByToken,
    className,
  } = props

  const { t } = useTranslation('app')
  const [edits, setEdits] = useState<Record<string, DraftEdit>>({})
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [hasLocalSaved, setHasLocalSaved] = useState(false)
  const timersRef = useRef<Map<string, number>>(new Map())
  const pendingRef = useRef<Map<string, DraftEdit>>(new Map())
  const onAutosaveRef = useRef(onAutosave)

  onAutosaveRef.current = onAutosave

  const setSize = drafts.length
  const hasUnconfirmed = drafts.some((draft) => {
    return !isCreativeDraftConfirmed(draft)
  })
  const hasDraftActions = Boolean(onAutosave || onConfirm || onCancel)
  const isReadOnly = !hasUnconfirmed || !hasDraftActions

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => {
        window.clearTimeout(timer)
      })
      timersRef.current.clear()
    }
  }, [])

  function readEdit(draft: AssistantCreativeDraft): DraftEdit {
    return edits[draft.key] ?? { title: draft.title, body: draft.body }
  }

  async function flushKey(key: string) {
    const timer = timersRef.current.get(key)
    if (timer) {
      window.clearTimeout(timer)
      timersRef.current.delete(key)
    }
    const pending = pendingRef.current.get(key)
    if (!pending) {
      return
    }
    pendingRef.current.delete(key)
    const save = onAutosaveRef.current
    if (!save) {
      return
    }
    await save(key, pending)
    setHasLocalSaved(true)
  }

  async function flushAll() {
    const keys = Array.from(pendingRef.current.keys())
    for (const key of keys) {
      await flushKey(key)
    }
  }

  function scheduleAutosave(key: string, patch: DraftEdit) {
    pendingRef.current.set(key, patch)
    const existing = timersRef.current.get(key)
    if (existing) {
      window.clearTimeout(existing)
    }
    const timer = window.setTimeout(() => {
      timersRef.current.delete(key)
      const pending = pendingRef.current.get(key)
      pendingRef.current.delete(key)
      if (pending && onAutosaveRef.current) {
        void onAutosaveRef.current(key, pending)
        setHasLocalSaved(true)
      }
    }, CREATIVE_DRAFT_AUTOSAVE_MS)
    timersRef.current.set(key, timer)
  }

  function handleEditChange(key: string, patch: DraftEdit) {
    setEdits((current) => ({ ...current, [key]: patch }))
    scheduleAutosave(key, patch)
  }

  async function handleConfirm() {
    await flushAll()
    if (onConfirm) {
      await onConfirm()
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel()
    }
  }

  const showSaved = isDraftSaved || hasLocalSaved

  const actionsRow = !isReadOnly ? (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="primary"
        size="sm"
        className="text-sm"
        data-testid="thread-creative-confirm"
        disabled={isConfirming}
        onClick={() => {
          void handleConfirm()
        }}
      >
        {t('threads.detail.creativeDraft.confirm')}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        data-testid="thread-creative-cancel"
        onClick={handleCancel}
      >
        {t('threads.detail.creativeDraft.cancel')}
      </Button>
      {showSaved ? (
        <Text
          size="xs"
          color="muted"
          data-testid="thread-creative-draft-saved"
        >
          {t('threads.detail.creativeDraft.draftSaved')}
        </Text>
      ) : null}
    </div>
  ) : null

  if (drafts.length === 0) {
    return null
  }

  return (
    <div
      className={cn('max-w-prose space-y-3', className)}
      data-testid="thread-creative-draft-set"
    >
      {drafts.map((draft, index) => {
        const edit = readEdit(draft)
        const isEditing = editingKey === draft.key
        const cardTitle = formatCreativeDraftTitle({
          letter: draft.letter,
          headline: edit.title,
          setSize,
          t,
        })

        return (
          <ThreadCreativeDraftCard
            key={draft.key}
            draft={draft}
            title={edit.title}
            body={edit.body}
            cardTitle={cardTitle}
            letter={draft.letter}
            index={index}
            isPeeking={!isEditing}
            isEditing={isEditing}
            canEdit={!isReadOnly}
            threadAssets={threadAssets}
            docsByToken={docsByToken}
            onEdit={() => {
              setEditingKey(draft.key)
            }}
            onDone={() => {
              setEditingKey((current) => {
                return current === draft.key ? null : current
              })
            }}
            onCancelEdit={() => {
              setEditingKey((current) => {
                return current === draft.key ? null : current
              })
            }}
            onExpand={onExpand}
            onTitleChange={(title) => {
              handleEditChange(draft.key, { title, body: edit.body })
            }}
            onBodyChange={(body) => {
              handleEditChange(draft.key, { title: edit.title, body })
            }}
          />
        )
      })}
      {actionsRow}
    </div>
  )
}
